/**
 * ExitIntentPopup - CRO component that shows a lead capture popup
 * when the user's mouse moves toward the browser chrome (exit intent)
 */
import { useState, useEffect, useCallback, forwardRef } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useLanguage } from '@/contexts/LanguageContextNew';

const STORAGE_KEY = 'callitdev_exit_popup_dismissed';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export const ExitIntentPopup = forwardRef<HTMLDivElement, object>(function ExitIntentPopup(_, ref) {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { language } = useLanguage();

  const dismiss = useCallback(() => {
    setVisible(false);
    try {
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {}
  }, []);

  useEffect(() => {
    // Don't show on admin/portal/auth routes
    if (window.location.pathname.match(/\/(admin|portal|agent|auth|checkout)\//)) return;

    // Check if already dismissed recently
    try {
      const ts = localStorage.getItem(STORAGE_KEY);
      if (ts && Date.now() - Number(ts) < DISMISS_DURATION_MS) return;
    } catch {}

    let triggered = false;
    const handler = (e: MouseEvent) => {
      if (triggered) return;
      if (e.clientY <= 5 && e.movementY < -10) {
        triggered = true;
        setVisible(true);
      }
    };

    // Delay listener to avoid triggering on initial page load
    const timer = setTimeout(() => {
      document.addEventListener('mousemove', handler);
    }, 5000);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousemove', handler);
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    // Track conversion via GTM dataLayer
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        event: 'exit_intent_lead',
        email_captured: true,
      });
    }
    setSubmitted(true);
    setTimeout(dismiss, 3000);
  };

  if (!visible) return null;

  const copy = EXIT_COPY[language] || EXIT_COPY.en;

  return (
    <div
      ref={ref}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
    >
      <div
        className="relative mx-4 w-full max-w-md rounded-2xl bg-navy border border-white/10 p-8 shadow-2xl animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={dismiss}
          className="absolute right-4 top-4 text-white/70 hover:text-white transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {submitted ? (
          <div className="text-center py-4">
            <div className="text-4xl mb-3">✅</div>
            <h3 className="text-xl font-bold text-white mb-2">{copy.thanks}</h3>
            <p className="text-white/80 text-sm">{copy.thanksDesc}</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <div className="text-3xl mb-2">📊</div>
              <h3 className="text-xl font-bold text-white mb-2">{copy.title}</h3>
              <p className="text-white/80 text-sm leading-relaxed">{copy.description}</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Input
                type="email"
                required
                placeholder={copy.placeholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder:text-white/70"
              />
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-orange to-orange-light text-white font-semibold"
              >
                {copy.cta}
              </Button>
            </form>

            <p className="mt-3 text-center text-xs text-white/70">{copy.privacy}</p>
          </>
        )}
      </div>
    </div>
  );
});

ExitIntentPopup.displayName = 'ExitIntentPopup';

const EXIT_COPY: Record<string, { title: string; description: string; placeholder: string; cta: string; privacy: string; thanks: string; thanksDesc: string }> = {
  en: {
    title: 'Wait! Get Your Free BPO Cost Analysis',
    description: "We'll show you exactly how much you can save by outsourcing to Morocco. No commitment required.",
    placeholder: 'Your professional email',
    cta: 'Get My Free Analysis',
    privacy: '🔒 Your data is protected. We never spam.',
    thanks: 'Thank You!',
    thanksDesc: "We'll send your personalized cost analysis within 24 hours.",
  },
  fr: {
    title: 'Attendez ! Obtenez votre analyse de coûts gratuite',
    description: 'Découvrez exactement combien vous pouvez économiser en externalisant au Maroc. Sans engagement.',
    placeholder: 'Votre email professionnel',
    cta: 'Recevoir mon analyse gratuite',
    privacy: '🔒 Vos données sont protégées. Jamais de spam.',
    thanks: 'Merci !',
    thanksDesc: 'Nous vous enverrons votre analyse personnalisée sous 24h.',
  },
  es: {
    title: '¡Espere! Obtenga su análisis de costos gratuito',
    description: 'Le mostraremos exactamente cuánto puede ahorrar externalizando a Marruecos. Sin compromiso.',
    placeholder: 'Su email profesional',
    cta: 'Obtener mi análisis gratuito',
    privacy: '🔒 Sus datos están protegidos. Nunca spam.',
    thanks: '¡Gracias!',
    thanksDesc: 'Le enviaremos su análisis personalizado en 24 horas.',
  },
  de: {
    title: 'Warten Sie! Kostenlose Kostenanalyse erhalten',
    description: 'Wir zeigen Ihnen genau, wie viel Sie durch Outsourcing nach Marokko sparen können. Unverbindlich.',
    placeholder: 'Ihre geschäftliche E-Mail',
    cta: 'Kostenlose Analyse erhalten',
    privacy: '🔒 Ihre Daten sind geschützt. Kein Spam.',
    thanks: 'Vielen Dank!',
    thanksDesc: 'Wir senden Ihnen Ihre personalisierte Kostenanalyse innerhalb von 24 Stunden.',
  },
  ar: {
    title: 'انتظر! احصل على تحليل التكاليف المجاني',
    description: 'سنوضح لك بالضبط كم يمكنك توفيره من خلال التعهيد إلى المغرب. بدون التزام.',
    placeholder: 'بريدك الإلكتروني المهني',
    cta: 'احصل على تحليلي المجاني',
    privacy: '🔒 بياناتك محمية. لا بريد مزعج أبداً.',
    thanks: 'شكراً لك!',
    thanksDesc: 'سنرسل لك تحليل التكاليف المخصص خلال 24 ساعة.',
  },
  it: {
    title: 'Aspetta! Ottieni la tua analisi gratuita dei costi BPO', description: 'Ti mostreremo esattamente quanto puoi risparmiare esternalizzando in Marocco. Senza impegno.',
    placeholder: 'La tua email professionale', cta: 'Ricevi la mia analisi gratuita', privacy: '🔒 I tuoi dati sono protetti. Mai spam.', thanks: 'Grazie!', thanksDesc: 'Ti invieremo la tua analisi personalizzata entro 24 ore.',
  },
  pt: {
    title: 'Espere! Obtenha sua análise de custos BPO gratuita', description: 'Mostraremos exatamente quanto você pode economizar terceirizando para o Marrocos. Sem compromisso.',
    placeholder: 'Seu email profissional', cta: 'Receber minha análise gratuita', privacy: '🔒 Seus dados estão protegidos. Nunca spam.', thanks: 'Obrigado!', thanksDesc: 'Enviaremos sua análise personalizada em 24 horas.',
  },
  nl: {
    title: 'Wacht! Ontvang uw gratis BPO-kostenanalyse', description: 'We laten u precies zien hoeveel u kunt besparen door uit te besteden aan Marokko. Vrijblijvend.',
    placeholder: 'Uw zakelijk e-mailadres', cta: 'Mijn gratis analyse ontvangen', privacy: '🔒 Uw gegevens zijn beschermd. Nooit spam.', thanks: 'Bedankt!', thanksDesc: 'We sturen uw gepersonaliseerde kostenanalyse binnen 24 uur.',
  },
  pl: {
    title: 'Zaczekaj! Pobierz bezpłatną analizę kosztów BPO', description: 'Pokażemy Ci dokładnie, ile możesz zaoszczędzić outsourcując do Maroka. Bez zobowiązań.',
    placeholder: 'Twój email służbowy', cta: 'Pobierz bezpłatną analizę', privacy: '🔒 Twoje dane są chronione. Nigdy spam.', thanks: 'Dziękujemy!', thanksDesc: 'Wyślemy Twoją spersonalizowaną analizę w ciągu 24 godzin.',
  },
  ru: {
    title: 'Подождите! Получите бесплатный анализ затрат на BPO', description: 'Мы покажем, сколько вы можете сэкономить при аутсорсинге в Марокко. Без обязательств.',
    placeholder: 'Ваш рабочий email', cta: 'Получить бесплатный анализ', privacy: '🔒 Ваши данные защищены. Никакого спама.', thanks: 'Спасибо!', thanksDesc: 'Мы отправим вам персонализированный анализ в течение 24 часов.',
  },
  zh: {
    title: '等等！获取免费BPO成本分析', description: '我们将向您展示外包到摩洛哥可以节省多少。无需承诺。',
    placeholder: '您的工作邮箱', cta: '获取免费分析', privacy: '🔒 您的数据受到保护。绝不发送垃圾邮件。', thanks: '谢谢！', thanksDesc: '我们将在24小时内发送您的个性化成本分析。',
  },
  ja: {
    title: 'お待ちください！無料BPOコスト分析を入手', description: 'モロッコへのアウトソーシングでどれだけ節約できるかをお見せします。義務なし。',
    placeholder: 'ビジネスメール', cta: '無料分析を受け取る', privacy: '🔒 データは安全に保護されます。スパムなし。', thanks: 'ありがとうございます！', thanksDesc: '24時間以内にパーソナライズされた分析をお送りします。',
  },
  tr: {
    title: 'Bekleyin! Ücretsiz BPO Maliyet Analizinizi Alın', description: "Fas'a dış kaynak kullanarak ne kadar tasarruf edebileceğinizi göstereceğiz. Taahhüt yok.",
    placeholder: 'İş e-postanız', cta: 'Ücretsiz analizimi al', privacy: '🔒 Verileriniz korunmaktadır. Asla spam yok.', thanks: 'Teşekkürler!', thanksDesc: 'Kişiselleştirilmiş maliyet analizinizi 24 saat içinde göndereceğiz.',
  },
  ko: {
    title: '잠깐! 무료 BPO 비용 분석을 받으세요', description: '모로코 아웃소싱으로 얼마나 절약할 수 있는지 정확히 보여드립니다. 약정 없음.',
    placeholder: '비즈니스 이메일', cta: '무료 분석 받기', privacy: '🔒 데이터는 안전하게 보호됩니다. 스팸 없음.', thanks: '감사합니다!', thanksDesc: '24시간 이내에 맞춤형 비용 분석을 보내드립니다.',
  },
  sv: {
    title: 'Vänta! Få din gratis BPO-kostnadsanalys', description: 'Vi visar dig exakt hur mycket du kan spara genom outsourcing till Marocko. Ingen förpliktelse.',
    placeholder: 'Din företags-e-post', cta: 'Få min gratis analys', privacy: '🔒 Dina data är skyddade. Aldrig spam.', thanks: 'Tack!', thanksDesc: 'Vi skickar din personliga kostnadsanalys inom 24 timmar.',
  },
  da: {
    title: 'Vent! Få din gratis BPO-omkostningsanalyse', description: 'Vi viser dig præcis, hvor meget du kan spare ved outsourcing til Marokko. Ingen forpligtelse.',
    placeholder: 'Din erhvervs-e-mail', cta: 'Få min gratis analyse', privacy: '🔒 Dine data er beskyttet. Aldrig spam.', thanks: 'Tak!', thanksDesc: 'Vi sender din personlige omkostningsanalyse inden for 24 timer.',
  },
};
